import React from 'react'
import { makeStyles } from 'transfix-ui/core/styles'
import VideoLightbox from './VideoLightbox'
import CarrierVideoData from './carriers.json'
import MarketplaceVideoData from './marketplace.json'
import { ReactComponent as ShipmentLifecycleSvg } from './shipment-lifecycle.svg'
import ShipperVideoData from './shippers.json'
import { ReactComponent as TransfixEcosystemSvg } from './transfix-ecosystem.svg'

const useDemosStyles = makeStyles(() => ({
  container: {
    backgroundImage: 'background.jpg',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    textAlign: 'center', // minWidth: '1280px',
    padding: '0px',
  },
  subtitleMarketplace: {
    color: '#56B784',
    fontFamily: 'Nunito Sans',
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },
  subtitle: {
    color: '#F5BF18',
    fontFamily: 'Nunito Sans',
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },
  subtitleShipper: {
    color: '#8FCAE9',
    fontFamily: 'Nunito Sans',
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },
  headline: {
    fontFamily: 'Exo 2',
    fontSize: '34px',
    fontWeight: 600,
    letterSpacing: '0.25px',
  },
  subHeadline: {
    fontFamily: 'Exo 2',
    fontSize: '24px',
    fontWeight: 500,
    letterSpacing: '0.15px',
  },
  text: {
    fontFamily: 'Nunito Sans',
    fontSize: '16px',
    fontWeight: 400,
    maxWidth: '814px',
  },
  blankSeparator: {
    width: '80px',
    height: '1px',
    marginTop: '45px',
    backgroundColor: 'transparent',
  },
  verticalSeparator: {
    zIndex: '1',
    width: '1px',
    borderColor: '#697684',
    borderLeftWidth: '0px',
    height: '150px',
    borderStyle: 'dashed',
    borderRightWidth: '2px',
    marginTop: '-40px',
  },
  shipperVerticalSeparator: {
    width: '1px',
    marginBottom: '15px',
    borderColor: '#697684',
    borderLeftWidth: '0px',
    height: '170px',
    borderStyle: 'dashed',
    borderRightWidth: '2px',
    marginTop: '-110px',
  },
  carrierVerticalSeparator: {
    width: '1px',
    marginBottom: '15px',
    borderColor: '#697684',
    borderLeftWidth: '0px',
    height: '170px',
    borderStyle: 'dashed',
    borderRightWidth: '2px',
    marginTop: '-160px',
  },
  separator: {
    width: '80px',
    height: '1px',
    backgroundColor: '#F5BF18',
    marginTop: '80px',
  },
  ecosystemDiagramContainer: {
    zIndex: '10',
    textAlign: 'center',
    width: '100%',
    paddingTop: '24px',
  },
  carrierSection: {
    width: '100vw',
    paddingTop: '40px',
    paddingBottom: '40px',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    backgroundColor: '#394657',
    background: 'linear-gradient(178deg, #394657 97%, #22292b calc(97% + 2px))',
  },
  shipperSection: {
    width: '100vw',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
  marketplaceSection: {
    width: '100vw',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    background: 'linear-gradient(178deg, #22292b 98%, #394657 calc(98% + 2px))',
    paddingBottom: '40px',
  },
  videos: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    gap: '32px 24px',
    paddingTop: '24px',
    paddingBottom: '24px',
    maxWidth: '816px',
  },
  dot: {
    height: '10px',
    width: '10px',
    backgroundColor: '#d9d9d9',
    borderRadius: '50%',
    display: 'inline-block',
    marginBottom: '25px',
  },
}))

interface IVideoData {
  id: string
  thumbnailId: string
  text: string
  title: string
}

export function Demos() {
  const classes = useDemosStyles()

  return (
    <div className={classes.container}>
      <hr className={classes.blankSeparator} />
      <p className={classes.headline}>
        Building the World&apos;s Most Connected and <br /> Intelligent Freight Platform
      </p>
      <p className={classes.text}>
        By combining enterprise-grade, machine-learning technology with intuitive software and
        dedicated supply chain experts, Transfix is enabling organizations to deliver with high
        performance and high reliability, drive long-term strategy and capacity planning, take empty
        miles off the road, and optimize their networks, at scale.
      </p>
      <hr className={classes.separator} />
      <p className={classes.subHeadline}>The Transfix Ecosystem</p>
      <p className={classes.text}>
        We drive modern supply chain performance and impact at scale with the next-generation in
        freight technology.
      </p>
      <div className={classes.ecosystemDiagramContainer}>
        <TransfixEcosystemSvg
          style={{
            maxWidth: '875px',
            width: '100%',
            borderRadius: '25px',
            maxHeight: '486px',
            height: 'max-content',
          }}
        />
      </div>
      <div className={classes.verticalSeparator} />
      <p className={classes.subHeadline}>See How Our AI-Driven Solutions Power Better Freight</p>
      <p className={classes.text}>
        Transfix Experts monitor the life of every shipment — they manage exceptions at scale with
        the help of automation, technology and AI to ensure shippers and carriers have a great
        experience.
      </p>
      <div className={classes.ecosystemDiagramContainer}>
        <ShipmentLifecycleSvg
          style={{
            maxWidth: '875px',
            width: '100%',
            borderRadius: '25px',
            maxHeight: '486px',
            height: 'max-content',
          }}
        />
      </div>
      <div className={classes.verticalSeparator} />
      {/* Marketplace */}
      <div className={classes.marketplaceSection}>
        <p className={classes.subtitleMarketplace}>Our Internal Marketplace Platform</p>
        <p className={classes.headline}>Built for Service & Efficiency</p>
        <p className={classes.text}>
          Developed and advanced by our team of engineers and data scientists, our core platform
          powers seamless service across the shipment&apos;s lifecycle, while driving efficiency
          through automation every step of the way.
        </p>
        <div className={classes.videos}>
          {MarketplaceVideoData.videos.map(({ id, text, thumbnailId, title }: IVideoData) => (
            <VideoLightbox
              key={id}
              videoId={id}
              videoThumbnailId={thumbnailId}
              videoTitle={title}
              videoText={text}
            />
          ))}
        </div>
      </div>
      {/* Carrier Platform */}
      <div className={classes.carrierSection}>
        <div className={classes.carrierVerticalSeparator} />
        <p className={classes.subtitle}>Our Carrier Web & Mobile Platforms</p>
        <p className={classes.headline}>Optimized for Carrier Success</p>
        <p className={classes.text}>
          Centered around intuitive workflows and real-time intelligence, our platform allows
          carriers to manage their fleet, improve trucking operations, and earn more for their
          business.
        </p>
        <div className={classes.videos}>
          {CarrierVideoData.videos.map(({ id, text, thumbnailId, title }: IVideoData) => (
            <VideoLightbox
              key={id}
              videoId={id}
              videoThumbnailId={thumbnailId}
              videoTitle={title}
              videoText={text}
            />
          ))}
        </div>
      </div>
      {/* Shipper Platform */}
      <div className={classes.shipperSection}>
        <div className={classes.shipperVerticalSeparator} />
        <p className={classes.subtitleShipper}>Our Shipper Platform</p>
        <p className={classes.headline}>Empowering Logistics Teams</p>
        <p className={classes.text}>
          Our inovative software allows shippers to procure capacity, tender shipments, and optimize
          their freight network. Shippers also gain access to proprietary tools that solve for
          today&apos;s complex freight challenges.
        </p>
        <div className={classes.videos}>
          {ShipperVideoData.videos.map(({ id, text, thumbnailId, title }: IVideoData) => (
            <VideoLightbox
              key={id}
              videoId={id}
              videoThumbnailId={thumbnailId}
              videoTitle={title}
              videoText={text}
            />
          ))}
        </div>
      </div>
      <div className={classes.verticalSeparator} />
      <div className={classes.dot} />
    </div>
  )
}
